import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sb } from '../supabase';
import { buildProductRef } from '../lib/factoryChat';
import { APP_STORE_URL } from '../lib/maabarContact';
import usePageTitle from '../hooks/usePageTitle';
import IdeaToProduct from '../components/IdeaToProduct';
import Footer from '../components/Footer';
import {
  HOME_CATEGORIES, MIXED_CATEGORY, CARDS_PER_CATEGORY, MOBILE_CARDS, MOBILE_QUERY, HOME_ROTATE_MS,
} from '../lib/homeCategories';
import { catalogSeed } from '../lib/catalogSeed';
import './home.css';
import { keepsLegacyCatalog, useHioBuyOnly } from '../lib/marketMode';

const APP_BANNER_DISMISSED = 'maabar.appBanner.dismissed';
// Rows pulled per category, in the catalog's own order (browse_products). The
// rotation walks through them a window at a time, so the surplus is what makes
// each tick show new cards.
const PER_CATEGORY_FETCH = 60;

const T = {
  ar: {
    banner: 'تصفّح أسرع في تطبيق معبر', bannerCta: 'افتح في التطبيق', bannerClose: 'إغلاق',
    heroBadge: 'الطلب المُدار',
    heroTitle: 'وكّل معبر بطلبك',
    heroSub: 'نبحث لك عن المورد، ونتفاوض ونتابع طلبك من المصنع إلى بابك.',
    heroCta: 'خلّ معبر يتولّى طلبك',
    flow: 'من المصنع إلى بابك',
    flowFactory: 'المصنع', flowShip: 'الشحن والمتابعة', flowHome: 'بابك',
    quoteTitle: 'ارفع طلبك للمصانع',
    quoteText: 'اكتب طلبك، وتصلك عروض مباشرة من المصانع — بلا وسطاء وبلا حاجز لغة.',
    quoteCta: 'اطلب عروض أسعار',
    ideaTitle: 'صنّع فكرتك',
    ideaText: 'منتج بتصميمك أو علامتك؟ احصل على عروض تصنيع.',
    ideaCta: 'ابدأ فكرتك',
    browse: 'تصفح الفئة',
    viewAll: 'عرض الكل',
    cardCta: 'اطلب عرض سعر',
    cardMsg: 'راسل المصنع',
    cardMsgBusy: 'جارٍ الفتح…',
    empty: 'لا توجد منتجات لعرضها في هذه الفئة الآن.',
    railPrev: 'المنتجات السابقة', railNext: 'المنتجات التالية',
    supEyebrow: 'للمصانع والمورّدين',
    supTitle: 'مصنع أو مورّد؟ اعرض منتجاتك للتجار السعوديين',
    supText: 'مجاناً — بلا عمولة ولا رسوم تسجيل',
    supCta: 'سجّل كمورّد',
    supSignin: 'عندك حساب مورّد؟ دخول',
  },
  en: {
    banner: 'Browse faster in the Maabar app', bannerCta: 'Open in app', bannerClose: 'Close',
    heroBadge: 'Managed order',
    heroTitle: 'Let Maabar handle it',
    heroSub: 'We find the supplier, negotiate, and follow your order from the factory to your door.',
    heroCta: 'Let Maabar handle your order',
    flow: 'From the factory to your door',
    flowFactory: 'Factory', flowShip: 'Shipping & tracking', flowHome: 'Your door',
    quoteTitle: 'Send your request to factories',
    quoteText: 'Write your request and factory offers come straight to you — no middlemen, no language barrier.',
    quoteCta: 'Request quotes',
    ideaTitle: 'Make your idea',
    ideaText: 'Your own design or brand? Get manufacturing offers.',
    ideaCta: 'Start your idea',
    browse: 'Browse category',
    viewAll: 'View all',
    cardCta: 'Request a quote',
    cardMsg: 'Message the factory',
    cardMsgBusy: 'Opening…',
    empty: 'No products to show in this category right now.',
    railPrev: 'Previous products', railNext: 'Next products',
    supEyebrow: 'For factories & suppliers',
    supTitle: 'Factory or supplier? Show your products to Saudi buyers',
    supText: 'Free — no commission, no signup fee',
    supCta: 'Register as a supplier',
    supSignin: 'Already a supplier? Sign in',
  },
  zh: {
    banner: '用 Maabar 应用浏览更快', bannerCta: '在应用中打开', bannerClose: '关闭',
    heroBadge: '托管订单',
    heroTitle: '把订单交给 Maabar',
    heroSub: '我们为您寻找供应商、议价，并跟进订单从工厂到您家门口。',
    heroCta: '让 Maabar 处理您的订单',
    flow: '从工厂到您家门口',
    flowFactory: '工厂', flowShip: '运输与跟踪', flowHome: '您的家门',
    quoteTitle: '向工厂发送需求',
    quoteText: '写下您的需求，工厂报价直接送达 — 没有中间商，没有语言障碍。',
    quoteCta: '索取报价',
    ideaTitle: '实现您的创意',
    ideaText: '有自己的设计或品牌？获取制造报价。',
    ideaCta: '开始您的创意',
    browse: '浏览该类目',
    viewAll: '查看全部',
    cardCta: '索取报价',
    cardMsg: '联系工厂',
    cardMsgBusy: '正在打开…',
    empty: '该类目暂无可展示的产品。',
    railPrev: '上一组产品', railNext: '下一组产品',
    supEyebrow: '工厂与供应商',
    supTitle: '工厂或供应商？向沙特买家展示您的产品',
    supText: '免费入驻 — 无佣金、无注册费',
    supCta: '注册为供应商',
    supSignin: '已有供应商账户？登录',
  },
};

// ── Flow icons ───────────────────────────────────────────────────────────────
const flowSvg = { width: 30, height: 30, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round' };
const FlowFactory = () => (<svg {...flowSvg}><path d="M3 21h18M4 21V10l5 3V10l5 3V6l6 4v11" /><path d="M8 21v-4h3v4" /></svg>);
const FlowBox = () => (<svg {...flowSvg}><path d="M3 8l9-5 9 5v8l-9 5-9-5V8z" /><path d="M3 8l9 5 9-5M12 13v8" /><path d="M9 11.5l2 2 3.5-3.5" /></svg>);
const FlowHome = () => (<svg {...flowSvg}><path d="M3 11l9-7 9 7" /><path d="M5 10v10h14V10" /><path d="M10 20v-6h4v6" /></svg>);
const IconDoc = () => (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5M9 13h6M9 17h4" /></svg>);
const IconBulb = () => (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18h6M10 21h4" /><path d="M12 3a6 6 0 0 0-3.5 10.9c.5.4.8 1 .8 1.6V16h5.4v-.5c0-.6.3-1.2.8-1.6A6 6 0 0 0 12 3z" /></svg>);
// The FILLED factory, identical path-for-path to the Supplier Login door in
// Navbar.jsx. Deliberately the same glyph: this card and that button are the
// same door, and a visitor who sees one then the other should recognise it.
//
// Duplicated rather than imported — Navbar keeps its icons local, this file
// keeps FlowFactory/IconDoc/IconBulb local, and a shared icon module is a bigger
// change than this card earns. Note there is now a SECOND, unfilled factory in
// this very file (FlowFactory, in the hero's from-factory-to-your-door strip);
// they are different glyphs on purpose — that one is a step in a journey, this
// one is an audience.
// 22px to match IconDoc and IconBulb, the two glyphs that already sit in a
// .home3-duo-icon square on this page.
const IconFactoryFilled = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <path d="M16 20V5h4v15z" fill="currentColor" />
    <path d="M3 20v-8l5.5 3.2V12l5.5 3.2V20z" fill="currentColor" />
  </svg>
);

const AUTOPLAY_MS = 5000;

// Both of these are module-level and take the element, rather than being closures
// inside ProductRail: a function declared in the component body and then used
// inside an effect has to be listed as a dependency of that effect, and this file
// is meant to add no new exhaustive-deps warnings.

// One card of travel: the distance between two adjacent cards, which is card
// width plus gap, measured from the DOM rather than assumed so it stays right at
// every breakpoint. Reading the delta works in both directions.
const railStep = (el) => {
  const first = el.firstElementChild;
  if (!first) return 0;
  const second = first.nextElementSibling;
  return second
    ? Math.abs(second.getBoundingClientRect().left - first.getBoundingClientRect().left)
    : first.getBoundingClientRect().width;
};

// RTL reports scrollLeft as 0 at the inline start and negative toward the end,
// so abs() reads the position in both directions. 2px of slack: fractional card
// widths mean the last card never lands exactly on max.
const railEdges = (el) => {
  const max = el.scrollWidth - el.clientWidth;
  const at = Math.abs(el.scrollLeft);
  return { atStart: at <= 2, atEnd: at >= max - 2 };
};

/**
 * Move the rail one card in `dir`, wrapping at either end. This is the single
 * mover: autoplay and both arrows call it, so the wrap behaves identically
 * however the rail was driven.
 *
 * The wrap is a jump to the opposite end, not a ring of cloned cards. Cloning
 * would give a seamless slide, but every card here holds two real controls — the
 * image button and the quote CTA — so a cloned edge would put duplicate,
 * clickable products in the tab order and read every one of them twice to a
 * screen reader. Keeping them out means `aria-hidden` plus `inert` plus click
 * forwarding on the clones, and re-cloning on every category switch, since
 * resetKey swaps the whole card set. A one-frame jump on a product row is a much
 * smaller cost than that, and it cannot desynchronise.
 *
 * 'instant' is what makes it a wrap rather than a rewind: the default smooth
 * behaviour sweeps the whole rail backwards past every card.
 */
const railAdvance = (el, dir, isAr) => {
  const step = railStep(el);
  if (!step) return;
  const sign = isAr ? -1 : 1;
  const { atStart, atEnd } = railEdges(el);
  if (dir > 0 && atEnd) {
    el.scrollTo({ left: 0, behavior: 'instant' });
  } else if (dir < 0 && atStart) {
    el.scrollTo({ left: sign * (el.scrollWidth - el.clientWidth), behavior: 'instant' });
  } else {
    el.scrollBy({ left: sign * dir * step });
  }
};

/**
 * The product row as a carousel. The scroll container is still the whole
 * mechanism — swipe, drag, trackpad and the arrow keys once it has focus all
 * drive it natively — and the two arrow buttons only move its scroll position,
 * so nothing depends on them. Every width snaps to `start`: six cards on
 * desktop, three on tablet, two on a phone. All of that lives in home.css.
 *
 * Autoplay nudges it one card every 5s, and is deliberately timid: it yields to
 * the pointer, to a hidden tab, to being scrolled off screen, and it gives up
 * for good the moment the visitor touches it — an arrow press counts as
 * touching it. Under prefers-reduced-motion it never starts.
 *
 * The rail is a loop: both arrows are always live, and past either end the row
 * wraps round to the other. See railAdvance for how, and why it is not done
 * with cloned cards.
 */
function ProductRail({ children, isAr, lang, label, resetKey }) {
  const railRef = useRef(null);
  // Sticky, and deliberately not reset by resetKey: once someone has taken hold
  // of the row, it should not start moving under them again on the next tab.
  const [engaged, setEngaged] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [onScreen, setOnScreen] = useState(false);
  const [tabVisible, setTabVisible] = useState(true);

  const reduceMotion = useMemo(() => {
    try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; }
    catch { return false; }   // older browsers: treat as no preference
  }, []);

  // Start at the first card: on mount, and again whenever the category changes
  // rather than inheriting where the previous tab was left. Instant, so it does
  // not animate a long sweep every time a tab is pressed.
  //
  // left: 0 is the INLINE start in both directions, not the left edge — under
  // the current CSSOM model an RTL container sits at 0 when it is scrolled hard
  // to the right and reads negative as it moves away. So this is also what pins
  // the Arabic rail to the right, in line with the heading. (The legacy model
  // where RTL started at scrollWidth was Chrome <85 and IE; on those this would
  // land at the far end instead.)
  useEffect(() => {
    const el = railRef.current;
    if (el) el.scrollTo({ left: 0, behavior: 'instant' });
  }, [resetKey]);

  // Autoplay scrolls the rail itself, so a scroll listener cannot tell us who
  // moved it. Input events can: these four are unambiguously the visitor.
  useEffect(() => {
    const el = railRef.current;
    if (!el) return undefined;
    const take = () => setEngaged(true);
    const events = ['pointerdown', 'touchstart', 'wheel', 'keydown'];
    events.forEach((e) => el.addEventListener(e, take, { passive: true }));
    return () => events.forEach((e) => el.removeEventListener(e, take));
  }, []);

  useEffect(() => {
    const read = () => setTabVisible(!document.hidden);
    read();
    document.addEventListener('visibilitychange', read);
    return () => document.removeEventListener('visibilitychange', read);
  }, []);

  useEffect(() => {
    const el = railRef.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === 'undefined') { setOnScreen(true); return undefined; }
    const io = new IntersectionObserver(([e]) => setOnScreen(e.isIntersecting), { threshold: 0.35 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (reduceMotion || engaged || hovered || !onScreen || !tabVisible) return undefined;
    const el = railRef.current;
    if (!el) return undefined;
    // Same mover as the arrows, so autoplay wraps exactly the way a press does.
    const id = window.setInterval(() => railAdvance(el, 1, isAr), AUTOPLAY_MS);
    return () => window.clearInterval(id);
  }, [reduceMotion, engaged, hovered, onScreen, tabVisible, isAr]);

  const c = T[lang] || T.ar;

  // One card per press, wrapping at either end. setEngaged matches what a touch
  // does: someone steering the row by hand has taken it over, and autoplay does
  // not resume.
  const nudge = (dir) => {
    const el = railRef.current;
    if (!el) return;
    setEngaged(true);
    railAdvance(el, dir, isAr);
  };

  const Arrow = ({ dir, label: aria }) => (
    <button
      type="button"
      className={`home3-arrow home3-arrow-${dir < 0 ? 'prev' : 'next'}`}
      onClick={() => nudge(dir)}
      aria-label={aria}
    >
      {/* One chevron, mirrored by CSS for the other side and again for RTL. */}
      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
        strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M15 5l-7 7 7 7" />
      </svg>
    </button>
  );

  return (
    // The wrapper is the positioning context for the arrows. They sit outside
    // the scroller — inside it they would scroll away with the cards.
    <div
      className="home3-rail"
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
    >
      <Arrow dir={-1} label={c.railPrev} />
      <div
        className="home3-grid"
        ref={railRef}
        // Focusable so the row is reachable and scrollable by keyboard; named by
        // its category so the stop is not an anonymous one.
        tabIndex={0}
        role="group"
        aria-label={label}
      >
        {children}
      </div>
      <Arrow dir={1} label={c.railNext} />
    </div>
  );
}

// The cards for one category, from the category's rows in catalog order
// (catalog ordering, owner 2026-10-03/04/11): featured first, then supplier
// products (a few per supplier), then a quality-weighted rotation drawn fresh
// on every page load.
//   - PRICED FIRST (owner, 2026-10-04, option A): products whose price the
//     trader sees (`priced` from browse_products) fill the row first; "price
//     on request" products only top it up when there are not enough
//     different priced sellers.
//   - Every card is from a DIFFERENT seller (factory or supplier), across both
//     passes. A category with fewer sellers than cards shows fewer cards -
//     never a repeat.
//   - IMAGES (owner, 2026-10-11): once image quality is switched on, only
//     main images judged good — then ok — appear here; bad and not-yet-scored
//     never do. The quality tier outranks priced-first. With the switch off,
//     only a bad image is kept out (no row carries a verdict then).
// Each rotation tick starts each walk a full row further on, wrapping at the
// end. `offset` is drawn once per load of the rows (owner, 2026-10-11: the
// first card was the same product on every refresh — the walk always started
// at the top of the list, which is the one featured product of the category).
// Line-for-line the app's pickCards (ProductCatalogSection.js).
export function pickCards(rows, rotation, count = CARDS_PER_CATEGORY, offset = 0) {
  const all = rows || [];
  // Once main images are scored (the switch is on, so rows carry
  // image_verdict), the home shows ONLY images judged good, then ok — never
  // bad and never not-yet-scored (owner, 2026-10-11: "I want high-quality
  // images"). Good fills the row first, priced before on-request inside it.
  // With the switch off no row carries a verdict, and only the old rule
  // applies: no bad image.
  const scored = all.some((p) => p.image_verdict);
  const seen = new Set();
  const out = [];
  const walk = (list) => {
    if (!list.length) return;
    const start = (rotation * count + offset) % list.length;
    for (let k = 0; k < list.length && out.length < count; k += 1) {
      const p = list[(start + k) % list.length];
      const seller = p.factory_id || p.seller_id;
      if (seen.has(seller)) continue;
      seen.add(seller);
      out.push(p);
    }
  };
  const tiers = scored
    ? [all.filter((p) => p.image_verdict === 'good'), all.filter((p) => p.image_verdict === 'ok')]
    : [all.filter((p) => p.image_verdict !== 'bad')];
  tiers.forEach((tier) => {
    walk(tier.filter((p) => p.priced === true));
    walk(tier.filter((p) => p.priced !== true));
  });
  return out;
}

// The "mixed" tab: each category's own picks, dealt round-robin — one card
// from each category in turn, starting from a different category each load
// and each tick — and still one card per seller across the whole row. Each
// card carries home_cat, the category it came from, for its links.
// Line-for-line the app's pickMixedCards (ProductCatalogSection.js).
export function pickMixedCards(rowsByCat, rotation, count = CARDS_PER_CATEGORY, offset = 0) {
  const lists = HOME_CATEGORIES.map((c) => pickCards((rowsByCat || {})[c.code], rotation, count, offset)
    .map((p) => ({ ...p, home_cat: c.code })));
  const first = (offset + rotation) % lists.length;
  const longest = Math.max(0, ...lists.map((l) => l.length));
  const seen = new Set();
  const out = [];
  for (let i = 0; i < longest && out.length < count; i += 1) {
    for (let j = 0; j < lists.length && out.length < count; j += 1) {
      const p = lists[(first + j) % lists.length][i];
      if (!p) continue;
      const seller = p.factory_id || p.seller_id;
      if (seen.has(seller)) continue;
      seen.add(seller);
      out.push(p);
    }
  }
  return out;
}

// One category's pool: the priced pool (browse_products p_priced_only) ahead
// of the normal one, each product once. The priced pool exists because the
// normal 60 can miss every priced product - auto parts' 12 priced products
// all come from one seller. Line-for-line the app's mergePools
// (useHomeProductPool.js).
export function mergePools(priced, normal) {
  const seen = new Set();
  const out = [];
  for (const p of [...(priced || []), ...(normal || [])]) {
    const key = `${p.source}:${p.id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(p);
  }
  return out;
}

export default function Home({ lang, user, profile, setProfile, displayCurrency, exchangeRates }) {
  const nav = useNavigate();
  const t = T[lang] || T.ar;
  const isAr = lang === 'ar';
  usePageTitle('home', lang);

  const [ideaOpen, setIdeaOpen] = useState(false);
  useEffect(() => () => setIdeaOpen(false), []);
  // HioBuy-only mode (lib/marketMode.js): customers are given the marketplace
  // home instead of this page (components/market/MarketHomeGate on "/"). This
  // guard only keeps the legacy rails from being fetched until the switch is known.
  const market = useHioBuyOnly();
  const legacyRails = market.ready && !(market.hioBuyOnly && !keepsLegacyCatalog(profile));

  // Read once before first paint so a phone never renders the long row and then
  // drops back to five — that would have fetched the images we are avoiding.
  const [isMobile, setIsMobile] = useState(() => {
    try { return window.matchMedia(MOBILE_QUERY).matches; }
    catch { return false; }   // no matchMedia: fall back to the full row
  });
  // Kept live rather than read once: a rotation or a resized desktop window
  // crosses the breakpoint, and the row should follow it.
  useEffect(() => {
    let mq;
    try { mq = window.matchMedia(MOBILE_QUERY); } catch { return undefined; }
    const read = () => setIsMobile(mq.matches);
    read();
    mq.addEventListener('change', read);
    return () => mq.removeEventListener('change', read);
  }, []);

  const [activeCat, setActiveCat] = useState(MIXED_CATEGORY.code);
  const [rowsByCat, setRowsByCat] = useState({});
  const [offset, setOffset] = useState(0);
  const [loadFailed, setLoadFailed] = useState(false);
  // Bumped every HOME_ROTATE_MS while the tab is visible; each bump re-picks
  // the cards from the rows already loaded (no new query).
  const [rotation, setRotation] = useState(0);
  // Per-card, not per-page: only the card being opened reads as busy.
  const [msgBusyId, setMsgBusyId] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const rootRef = useRef(null);

  // App banner: Android and other mobile browsers only. iOS gets Apple's own
  // Smart App Banner from the meta tag in index.html, and showing both would
  // stack two banners on one screen.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return;
    const ua = navigator.userAgent || '';
    const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(ua);
    const isIOS = /iPhone|iPad|iPod/i.test(ua);
    if (!isMobile || isIOS) return;
    try {
      if (window.localStorage.getItem(APP_BANNER_DISMISSED) === '1') return;
    } catch {
      // Private mode or blocked storage: show it, just do not remember.
    }
    setShowBanner(true);
  }, []);

  const dismissBanner = () => {
    setShowBanner(false);
    try { window.localStorage.setItem(APP_BANNER_DISMISSED, '1'); } catch { /* not fatal */ }
  };

  // One bounded query per category rather than one big one. A single capped
  // query ordered only by is_featured leaves the rest of the ordering to the
  // planner, so the small categories (auto parts has 143 candidates against
  // apparel's 999) can fall outside the cap entirely and render an empty tab.
  // Five small requests cost less than the payload a cap large enough to be
  // safe would have needed.
  //
  // Errors are surfaced, never swallowed: a failed category logs its reason and
  // the tab falls back to its still image instead of rendering blank.
  useEffect(() => {
    if (!legacyRails) return undefined;
    let cancelled = false;
    (async () => {
      const seed = catalogSeed(user?.id);
      const results = await Promise.all(HOME_CATEGORIES.map(async (c) => {
        const ask = (pricedOnly) => sb.rpc('browse_products', {
          p_categories: null, p_subcategories: c.allSubs, p_search: null, p_after: null,
          p_limit: PER_CATEGORY_FETCH, p_seed: seed, p_priced_only: pricedOnly,
        });
        const [pricedRes, normalRes] = await Promise.all([ask(true), ask(false)]);
        if (pricedRes.error) console.error(`[Home] priced product query failed for "${c.code}":`, pricedRes.error);
        if (normalRes.error) {
          console.error(`[Home] product query failed for "${c.code}":`, normalRes.error);
          return { code: c.code, failed: true, rows: [] };
        }
        // browse_products names the seller `factory_id` for both catalogs; the
        // supplier chat path reads `seller_id`.
        const rows = mergePools(pricedRes.data, normalRes.data)
          .filter((p) => typeof p.image === 'string' && /^https?:\/\//.test(p.image))
          .map((p) => (p.source === 'supplier' ? { ...p, seller_id: p.factory_id } : p));
        return { code: c.code, failed: false, rows };
      }));
      if (cancelled) return;
      // A new starting point every time the rows load (a page load), so the
      // first card is not the same product on every refresh.
      setOffset(Math.floor(Math.random() * 1000));
      setRowsByCat(Object.fromEntries(results.map((r) => [r.code, r.rows])));
      setLoadFailed(results.some((r) => r.failed));
    })();
    return () => { cancelled = true; };
  }, [user?.id, legacyRails]);

  useEffect(() => {
    const id = window.setInterval(() => {
      if (!document.hidden) setRotation((r) => r + 1);
    }, HOME_ROTATE_MS);
    return () => window.clearInterval(id);
  }, []);

  const cardsByCat = useMemo(() => {
    const out = {};
    HOME_CATEGORIES.forEach((c) => { out[c.code] = pickCards(rowsByCat[c.code], rotation, CARDS_PER_CATEGORY, offset); });
    out[MIXED_CATEGORY.code] = pickMixedCards(rowsByCat, rotation, CARDS_PER_CATEGORY, offset);
    return out;
  }, [rowsByCat, rotation, offset]);

  const tabs = [MIXED_CATEGORY, ...HOME_CATEGORIES];
  const category = tabs.find((c) => c.code === activeCat) || MIXED_CATEGORY;
  // The catalogue link for a category; the mixed tab has none of its own.
  const catLink = (code) => (code && code !== MIXED_CATEGORY.code ? `/products?cat=${code}` : '/products');
  // Sliced, not hidden: a card that is never rendered never requests its image,
  // which is the whole point of the shorter mobile row. pickCards already orders
  // featured-first and one per named subcategory, so the five that survive are
  // the most curated five rather than an arbitrary head of the list.
  const cards = useMemo(() => {
    const all = cardsByCat[category.code] || [];
    return isMobile ? all.slice(0, MOBILE_CARDS) : all;
  }, [cardsByCat, category.code, isMobile]);

  const productName = (p) => (lang === 'zh' ? (p.name_zh || p.name_en || p.name_ar)
    : lang === 'en' ? (p.name_en || p.name_ar || p.name_zh)
      : (p.name_ar || p.name_en || p.name_zh)) || '';

  // factory_products open at /factory/:fid/product/:pid. /products/:id is
  // ProductDetail, which reads the `products` table — right for a supplier's
  // own product, "not found" for a factory one.
  const openProduct = (p) => {
    if (p.source === 'supplier') nav(`/products/${p.id}`);
    else if (p.factory_id) nav(`/factory/${p.factory_id}/product/${p.id}`);
    else nav(catLink(p.home_cat || category.code));
  };
  // The factory's own quote form, prefilled with the product — the same entry
  // the catalogue uses. /request is the managed wizard and would quietly turn a
  // "quote me this sofa" into a managed sourcing brief. A supplier's product
  // has its purchase request on its own page.
  const requestQuote = (p) => {
    if (p.source === 'supplier') nav(`/products/${p.id}`);
    else if (p.factory_id) nav(`/factory/${p.factory_id}?request=1`);
    else nav(catLink(p.home_cat || category.code));
  };
  // Direct chat with this product's factory — the same navigation the product
  // detail page uses (FactoryProductDetail.jsx:71). Chat is unified onto the
  // `messages` table, so there is no thread to create and no RPC to call: a
  // buyer->factory message is addressed receiver_id NULL + factory_id and
  // resolves to the owner via factory_directory.linked_supplier_id. The product
  // rides along in route state and Chat.jsx attaches it to the first message.
  const messageFactory = (p) => {
    const supplier = p.source === 'supplier';
    if (msgBusyId || (!supplier && !p.factory_id) || (supplier && !p.seller_id)) return;
    setMsgBusyId(p.id);
    // Signed-out: same as every other factory-chat entry on web — straight to
    // /login. (The app stashes the intent and replays it after auth; web has no
    // equivalent yet, so this deliberately matches the existing web behaviour
    // rather than inventing a second one here.)
    if (!user) { nav('/login'); return; }
    // A supplier's own product: the direct supplier chat ProductDetail opens.
    if (supplier) { nav(`/chat/${p.seller_id}`, { state: { product: buildProductRef(p) } }); return; }
    nav(`/chat/f/${p.factory_id}`, { state: { product: buildProductRef(p) } });
  };

  // A storage URL can 404 long after the row was written. Swap in the category
  // still rather than leave a broken frame.
  const onImgError = (e) => {
    if (e.currentTarget.dataset.fellBack) return;
    e.currentTarget.dataset.fellBack = '1';
    e.currentTarget.src = category.image;
  };

  const arFont = isAr ? { fontFamily: 'var(--font-ar)' } : {};

  // Defined once and placed in one of TWO positions rather than written twice:
  // two copies would drift, and the difference between the languages is WHERE it
  // sits, not what it says.
  // A <section> WRAPPING an <article>, mirroring .home3-duo exactly, rather than
  // a bare <section>. Both for rhythm and because of a real bug: `.home3 >
  // section { padding: 0; background: none }` (home.css:22) resets every direct
  // section child, and at (0,1,1) it out-specifies any single class. The first
  // version of this card WAS a bare section and shipped to production with its
  // background and padding stripped — white text on nothing. The duo cards were
  // never affected because they are articles inside a section, which is the
  // structure this now copies.
  const supplierCta = (
    <section className={`home3-supwrap${lang === 'zh' ? ' home3-supwrap--lead' : ''}`}>
      <article className="home3-duo-card home3-supplier">
        {/* Icon square LEADING, copy beside it — not above it. home3-duo-icon is
            the duo cards' own class, so the square is identical: 44px, 12px
            radius, #F4EFE7 on #6B6259. Under dir="rtl" the flex row reverses on
            its own, so the square leads on the right in Arabic with no extra
            rule. */}
        <div className="home3-supplier-main">
          <span className="home3-duo-icon"><IconFactoryFilled /></span>
          <div className="home3-supplier-copy">
            <span className="home3-supplier-eyebrow" style={arFont}>{t.supEyebrow}</span>
            <h2 style={arFont}>{t.supTitle}</h2>
            <p style={arFont}>{t.supText}</p>
          </div>
        </div>
        <div className="home3-supplier-actions">
          {/* Both go to /login/supplier — the same page serves sign-in and
              application (Login.jsx reads useParams().role). No route or auth
              change; the second link exists because a returning supplier should
              not have to read a registration button and wonder. */}
          <button className="home3-supplier-cta" onClick={() => nav('/login/supplier')} style={arFont}>
            {t.supCta}
          </button>
          <button className="home3-supplier-signin" onClick={() => nav('/login/supplier')} style={arFont}>
            {t.supSignin}
          </button>
        </div>
      </article>
    </section>
  );

  return (
    <>
    <div ref={rootRef} className={`home3${isAr ? ' rtl' : ''}`} dir={isAr ? 'rtl' : 'ltr'}>
      {showBanner && (
        <div className="home3-appbanner">
          <span style={arFont}>{t.banner}</span>
          <a className="home3-appbanner-cta" href={APP_STORE_URL} target="_blank" rel="noreferrer" style={arFont}>
            {t.bannerCta}
          </a>
          <button className="home3-appbanner-x" onClick={dismissBanner} aria-label={t.bannerClose}>×</button>
        </div>
      )}

      {/* ⚠️ ZH LEADS WITH THE SUPPLIER BAND, ON PURPOSE. Not a layout accident,
          not a direction artifact, and NOT a bug to normalise away.

          The Chinese surface of this site exists for the factories. A Chinese
          factory landing here should not have to scroll past a hero written to
          persuade Saudi BUYERS in order to find its own door — so on zh, and
          only on zh, that door comes first. Arabic and English keep the hero
          first and take the band after it; that mount is further down.

          ⚠️ THIS WAS REMOVED AND RESTORED ON 2026-09-22, both within a day: the
          two mounts were collapsed into one "because section order must not
          depend on language", and the owner put the split back — the whole
          point is that the audience differs by language, so the order does too.
          Cost of the round trip: this comment. Read it before the next tidy-up.

          The paired margin lives on .home3-supwrap--lead in home.css: leading
          the page, the band needs its gap BELOW rather than above. */}
      {lang === 'zh' && supplierCta}

      {/* ── Hero ── */}
      <section className="home3-hero">
        <picture>
          <source media="(max-width: 720px)" srcSet="/images/hero/maabar-hero-mobile.webp" />
          <img
            className="home3-hero-bg"
            src="/images/hero/maabar-hero-desktop.webp"
            alt=""
            loading="eager"
            fetchpriority="high"
            decoding="async"
          />
        </picture>
        <div className="home3-hero-veil" />
        <div className="home3-hero-inner">
          <div className="home3-hero-text">
            <span className="home3-badge" style={arFont}>{t.heroBadge}</span>
            <h1 className="home3-title" style={arFont}>{t.heroTitle}</h1>
            <p className="home3-sub" style={arFont}>{t.heroSub}</p>
            <button className="home3-cta" onClick={() => nav('/request')} style={arFont}>
              {t.heroCta} <span aria-hidden="true">{isAr ? '←' : '→'}</span>
            </button>
          </div>
          <div className="home3-flow" aria-label={t.flow}>
            <div className="home3-flow-row">
              <span className="home3-flow-step"><FlowFactory /><em style={arFont}>{t.flowFactory}</em></span>
              <span className="home3-flow-dots" aria-hidden="true" />
              <span className="home3-flow-step"><FlowBox /><em style={arFont}>{t.flowShip}</em></span>
              <span className="home3-flow-dots" aria-hidden="true" />
              <span className="home3-flow-step"><FlowHome /><em style={arFont}>{t.flowHome}</em></span>
            </div>
            <p className="home3-flow-cap" style={arFont}>{t.flow}</p>
          </div>
        </div>
      </section>

      {/* ── Suppliers ──
          Suppliers were landing on a buyer-facing home page and registering as
          buyers — it happened on 2026-09-17 (Ninghai Zhengjie) and had to be
          converted by hand. The header now has two labelled doors; this is the
          same door stated in full, because someone who scrolled past the header
          has already missed it once.

          It sits BEFORE the buyer cards on every language, and before the HERO
          on zh: the Chinese surface exists for the factories, so on that page
          this is the first thing rather than the third. See supplierCta below. */}
      {/* ⚠️ THE OTHER HALF OF A DELIBERATE, TWICE-CONFIRMED PLACEMENT.
          This renders the band for ar and en only; zh gets it ABOVE the hero,
          about sixty lines up. The reason is on that mount — read it before
          touching either.

          On 2026-09-22 this was flattened to a single unconditional mount
          "because section order should not depend on language", and the owner
          reversed that the same day: the asymmetry is the product decision, not
          a bug in it. If it looks wrong again, the answer is the comment above
          the zh mount, not this line. */}
      {lang !== 'zh' && supplierCta}

      {/* ── Two entry points ── */}
      <section className="home3-duo">
        <article className="home3-duo-card">
          <div className="home3-duo-head">
            <span className="home3-duo-icon"><IconDoc /></span>
            <h2 style={arFont}>{t.quoteTitle}</h2>
          </div>
          <p style={arFont}>{t.quoteText}</p>
          <button className="home3-ghost" onClick={() => nav('/requests?mode=direct')} style={arFont}>{t.quoteCta}</button>
        </article>
        <article className="home3-duo-card">
          <div className="home3-duo-head">
            <span className="home3-duo-icon"><IconBulb /></span>
            <h2 style={arFont}>{t.ideaTitle}</h2>
          </div>
          <p style={arFont}>{t.ideaText}</p>
          <button className="home3-ghost" onClick={() => setIdeaOpen(true)} style={arFont}>{t.ideaCta}</button>
        </article>
      </section>

      {/* ── Categories ── (legacy rails) */}
      {!legacyRails ? null : (
      <section className="home3-cats">
        <div className="home3-tabs" role="tablist" aria-label={t.browse}>
          {tabs.map((c) => (
            <button
              key={c.code}
              role="tab"
              aria-selected={c.code === activeCat}
              className={`home3-tab${c.code === activeCat ? ' is-on' : ''}`}
              onClick={() => setActiveCat(c.code)}
              style={arFont}
            >
              {c.tab[lang] || c.tab.ar}
            </button>
          ))}
          <button className="home3-tab home3-tab-all" onClick={() => nav('/products')} style={arFont}>
            {t.viewAll}
          </button>
        </div>

        <div className="home3-cats-head">
          <h2 style={arFont}>{category.title[lang] || category.title.ar}</h2>
          <button className="home3-browse" onClick={() => nav(catLink(category.code))} style={arFont}>
            {t.browse} <span aria-hidden="true">{isAr ? '←' : '→'}</span>
          </button>
        </div>

        {cards.length === 0 ? (
          <div className="home3-fallback">
            <img src={category.image} alt="" loading="lazy" />
            <p style={arFont}>{t.empty}</p>
            <button className="home3-ghost" onClick={() => nav(catLink(category.code))} style={arFont}>
              {t.browse}
            </button>
          </div>
        ) : (
          <ProductRail isAr={isAr} lang={lang} resetKey={category.code} label={category.title[lang] || category.title.ar}>
            {cards.map((p) => (
              <article key={p.id} className="home3-card">
                <button
                  className="home3-card-img"
                  onClick={() => openProduct(p)}
                  aria-label={productName(p)}
                >
                  <img src={p.image} alt={productName(p)} loading="lazy" decoding="async" onError={onImgError} />
                </button>
                <h3 className="home3-card-name" style={arFont}>{productName(p)}</h3>
                <button
                  className="home3-card-msg"
                  onClick={() => messageFactory(p)}
                  disabled={msgBusyId === p.id}
                  style={arFont}
                >
                  {msgBusyId === p.id ? t.cardMsgBusy : t.cardMsg}
                </button>
                <button className="home3-ghost home3-card-cta" onClick={() => requestQuote(p)} style={arFont}>
                  {t.cardCta}
                </button>
              </article>
            ))}
          </ProductRail>
        )}

        {loadFailed && (
          <p className="home3-note" style={arFont} role="status">{t.empty}</p>
        )}
      </section>
      )}

      {ideaOpen && (
        <IdeaToProduct lang={lang} user={user} profile={profile} setProfile={setProfile} displayCurrency={displayCurrency} onClose={() => setIdeaOpen(false)} />
      )}
    </div>
    {/* Outside .home3 on purpose: that div is the 1200px column, and every other
        page renders the footer as a full-bleed band beside its wrapper, not
        inside it. Direction comes from the app shell (App.js), which already
        sets dir, and the footer picks its own alignment from `lang`. */}
    <Footer lang={lang} />
    </>
  );
}
